/**
 * wav.ts — minimal 16-bit PCM mono WAV encoder → base64 data URI.
 *
 * Game sounds are synthesised offline (see synth.ts) into Float32 PCM, encoded
 * here into a WAV data URI, and handed to Howler for playback (see player.ts).
 * WAV is used (not MP3) because it needs no encoder dependency and the clips
 * are short; a data URI lets Howler load it with no network request.
 */

const WAV_HEADER_BYTES = 44;
const BYTES_PER_SAMPLE = 2; // 16-bit
const PCM_FORMAT = 1;
const NUM_CHANNELS = 1; // all game sounds are mono
const INT16_MAX = 0x7fff;
const INT16_MIN_ABS = 0x8000;
const BASE64_CHUNK = 0x8000; // avoids call-stack limits in String.fromCharCode

/** Encodes mono Float32 PCM (range -1..1) into 16-bit WAV bytes. */
function floatToWavBytes(samples: Float32Array, sampleRate: number): Uint8Array {
  const dataSize = samples.length * BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataSize);
  const view = new DataView(buffer);

  let offset = 0;
  const writeString = (s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset++, s.charCodeAt(i));
  };
  const writeUint32 = (v: number) => {
    view.setUint32(offset, v, true);
    offset += 4;
  };
  const writeUint16 = (v: number) => {
    view.setUint16(offset, v, true);
    offset += 2;
  };

  // RIFF chunk descriptor
  writeString('RIFF');
  writeUint32(WAV_HEADER_BYTES - 8 + dataSize);
  writeString('WAVE');

  // fmt sub-chunk
  writeString('fmt ');
  writeUint32(16); // sub-chunk size for PCM
  writeUint16(PCM_FORMAT);
  writeUint16(NUM_CHANNELS);
  writeUint32(sampleRate);
  writeUint32(sampleRate * NUM_CHANNELS * BYTES_PER_SAMPLE); // byte rate
  writeUint16(NUM_CHANNELS * BYTES_PER_SAMPLE); // block align
  writeUint16(BYTES_PER_SAMPLE * 8); // bits per sample

  // data sub-chunk
  writeString('data');
  writeUint32(dataSize);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, clamped < 0 ? clamped * INT16_MIN_ABS : clamped * INT16_MAX, true);
    offset += BYTES_PER_SAMPLE;
  }

  return new Uint8Array(buffer);
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i += BASE64_CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(i, i + BASE64_CHUNK));
  }
  return btoa(binary);
}

/** Encodes mono Float32 PCM into a `data:audio/wav;base64,...` URI. */
export function floatToWavDataUri(samples: Float32Array, sampleRate: number): string {
  return `data:audio/wav;base64,${bytesToBase64(floatToWavBytes(samples, sampleRate))}`;
}

/** Encodes the mono channel of a rendered AudioBuffer into a WAV data URI. */
export function audioBufferToWavDataUri(buffer: AudioBuffer): string {
  return floatToWavDataUri(buffer.getChannelData(0), buffer.sampleRate);
}
